import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import * as Icons from '~/client/src/shared/components/Icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import Delivery from '~/client/src/shared/models/Delivery'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import { MaterialsDataKeys } from '../MaterialsDataKeys'
import { IFormCellData } from '../MaterialsList.store'
import BaseDataCell from './BaseDataCell/BaseDataCell'
import DeliveryDataCellTable from './DeliveryDataCellTable'

// localization: translated

interface IProps {
  dataKey: string
  deliveryCellData: IFormCellData
  onNewDeliveryClick(viewModelId: string): void
  onIdClick(deliveryId: string): void
  locationRenderer(location: LocationBase): JSX.Element
  companyRenderer(company: Company): JSX.Element

  isNewBtnAvailable?: boolean

  companiesStore?: CompaniesStore
  projectDateStore?: ProjectDateStore
}

const deliveryCellColumnsIds = [
  MaterialsDataKeys.NEXT_DELIVERY_BOOKING_DATE,
  MaterialsDataKeys.PLANNED_QUANTITY,
  MaterialsDataKeys.STATUS,
  MaterialsDataKeys.CURRENT_MATERIAL_LOCATION,
]
const deliveryAndDateIds: string[] = [
  MaterialsDataKeys.NEXT_DELIVERY,
  MaterialsDataKeys.NEXT_DELIVERY_BOOKING_DATE,
]

const isDeliveryDone = (delivery: Delivery): boolean =>
  delivery.isOnSiteOrInspected
const isDeliveryInRange = (
  delivery: Delivery,
  startDate: Date,
  endDate: Date,
): boolean => delivery.isScheduledWithinRange(startDate, endDate)

@inject('companiesStore', 'projectDateStore')
@observer
export default class DeliveryDataCell extends React.Component<IProps> {
  @observable private deliveryCellData
  @observable private locationRenderer
  @observable private companyRenderer
  @observable private onIdClick
  @observable private dataKey
  public static defaultProps = {
    deliveryCellData: {},
  }

  public constructor(props: IProps) {
    super(props)

    this.deliveryCellData = props.deliveryCellData
    this.locationRenderer = props.locationRenderer
    this.companyRenderer = props.companyRenderer
    this.onIdClick = props.onIdClick
    this.dataKey = props.dataKey
  }

  public render() {
    const { relatedDeliveries, itemsCount } = this.props.deliveryCellData

    return (
      <BaseDataCell<Delivery>
        items={relatedDeliveries}
        newItemIcon={
          <Icons.Delivery className="form-modal-add-icon relative no-flex mr5" />
        }
        newItemText={Localization.translator.newDelivery}
        onNewItemClick={this.onNewDeliveryClick}
        renderActiveItems={this.renderActiveDeliveriesTable}
        renderDoneItems={this.renderDoneDeliveriesTable}
        doneFilter={isDeliveryDone}
        dateRangeFilter={isDeliveryInRange}
        isNewValueShown={this.props.isNewBtnAvailable && this.isDeliveryCell}
        isTotalDoneValueShown={this.isDeliveryCell}
        isDateRangeShown={this.isDeliveryOrDateCell}
        doneTableText={Localization.translator.doneDeliveries}
        getTotalDoneValue={this.getTotalDeliveredQuantity}
      >
        <div
          className={classList({
            'row pointer h48 no-select': true,
            'underline-hover': !!itemsCount,
          })}
        >
          <div className="text-ellipsis">{this.cellLabel}</div>
          <ChevronDownIcon size={12} className="ml5" />
        </div>
      </BaseDataCell>
    )
  }

  private get cellLabel(): string | JSX.Element {
    const { itemsCount } = this.props.deliveryCellData

    if (!itemsCount) {
      return NO_VALUE
    }
    return itemsCount > 1 ? this.xItemsLabel : this.oneItemLabel
  }

  private get xItemsLabel(): string {
    const {
      deliveryCellData: { itemsCount },
      dataKey,
    } = this.props
    switch (dataKey) {
      case MaterialsDataKeys.NEXT_DELIVERY:
        return Localization.translator.xDeliveries(itemsCount)
      case MaterialsDataKeys.NEXT_DELIVERY_BOOKING_DATE:
        return Localization.translator.xDates(itemsCount)
      case MaterialsDataKeys.VENDOR:
        return Localization.translator.xVendors(itemsCount)
      case MaterialsDataKeys.RESPONSIBLE_COMPANY:
        return Localization.translator.xCompanies(itemsCount)
      default:
        return null
    }
  }

  private get oneItemLabel(): JSX.Element | string {
    const {
      companiesStore,
      projectDateStore: { getMonthDayYearAndTimeToDisplay },
      dataKey,
      companyRenderer,
    } = this.props

    const delivery = this.allActiveDeliveries[0]
    if (!delivery) {
      return null
    }
    switch (dataKey) {
      case MaterialsDataKeys.NEXT_DELIVERY:
        return delivery.codeToDisplay(companiesStore)
      case MaterialsDataKeys.NEXT_DELIVERY_BOOKING_DATE:
        return getMonthDayYearAndTimeToDisplay(delivery.startDate)
      case MaterialsDataKeys.VENDOR:
        return companyRenderer(companiesStore.getCompanyById(delivery.vendor))
      case MaterialsDataKeys.RESPONSIBLE_COMPANY:
        return companyRenderer(companiesStore.getCompanyById(delivery.company))
      default:
        return null
    }
  }

  private renderActiveDeliveriesTable = (
    activeDeliveries: Delivery[],
  ): JSX.Element => {
    return (
      <DeliveryDataCellTable
        isFooterHidden={!this.isDeliveryCell}
        shownColumnIds={this.shownColumnIds}
        deliveries={activeDeliveries}
        procurementId={this.deliveryCellData.procurementId}
        materialId={this.deliveryCellData.materialId}
        footerTitle={Localization.translator.totalBooked}
        locationRenderer={this.locationRenderer}
        companyRenderer={this.companyRenderer}
        onIdClick={this.onIdClick}
      />
    )
  }

  private renderDoneDeliveriesTable = (
    doneDeliveries: Delivery[],
  ): JSX.Element => {
    const { deliveryCellData, locationRenderer, companyRenderer, onIdClick } =
      this.props

    return (
      <DeliveryDataCellTable
        isHeaderHidden={true}
        isFooterHidden={!this.isDeliveryCell}
        shownColumnIds={this.shownColumnIds}
        deliveries={doneDeliveries}
        procurementId={deliveryCellData.procurementId}
        materialId={deliveryCellData.materialId}
        footerTitle={Localization.translator.totalDelivered}
        locationRenderer={locationRenderer}
        companyRenderer={companyRenderer}
        onIdClick={onIdClick}
      />
    )
  }

  private get allActiveDeliveries(): Delivery[] {
    const { relatedDeliveries } = this.deliveryCellData
    return this.isDeliveryOrDateCell
      ? relatedDeliveries.filter(d => !d.isOnSiteOrInspected)
      : relatedDeliveries
  }

  private getTotalDeliveredQuantity = (
    doneDeliveries: Delivery[],
  ): React.ReactText => {
    const { procurementId, materialId } = this.deliveryCellData
    return doneDeliveries.reduce(
      (sum, del) =>
        (sum +=
          (procurementId
            ? del.procurementQuantitiesMap[procurementId]
            : del.materialQuantitiesMap[materialId]) || 0),
      0,
    )
  }

  private get shownColumnIds(): string[] {
    return this.isDeliveryCell ? deliveryCellColumnsIds : [this.dataKey]
  }

  private get isDeliveryCell(): boolean {
    return this.dataKey === MaterialsDataKeys.NEXT_DELIVERY
  }

  private get isDeliveryOrDateCell(): boolean {
    return deliveryAndDateIds.includes(this.dataKey)
  }

  private onNewDeliveryClick = () => {
    const { onNewDeliveryClick, deliveryCellData } = this.props
    onNewDeliveryClick(deliveryCellData.id)
  }
}
